/**
 * A number with exactly `decimals` decimal places, in the reader's locale: `useDateFormat()`'s
 * `numberLocale`, whose region is the reader's country. "1,234.5" for an account in the States,
 * "1.234,5" for one in Germany whatever language it reads in, which is how that country writes a
 * number. `toFixed` wrote the American form for everyone.
 */
export function formatDecimal(value: number, locale: string, decimals: number): string {
  return new Intl.NumberFormat(locale, {minimumFractionDigits: decimals, maximumFractionDigits: decimals}).format(value);
}

/**
 * A price the backend gives in cents, in its own currency, written the way the reader's country
 * writes money (`useDateFormat().numberLocale`): "€9.99" in the States, "9,99 €" in German in Germany. Falls
 * back to the bare code and amount for a currency `Intl` does not know.
 */
export function formatCurrency(cents: number, currency: string, locale: string): string {
  try {
    return new Intl.NumberFormat(locale, {style: 'currency', currency: currency.toUpperCase()}).format(cents / 100);
  } catch {
    return `${currency} ${cents / 100}`;
  }
}

/**
 * A percentage given as a number of percent (12.5, not 0.125), the way the reader's country writes
 * one (`useDateFormat().numberLocale`): "12.5%" in the States, "12,5 %" in Germany. `signed` marks a
 * change, "+12%" or "-3%", and leaves no change unsigned.
 */
export function formatPercentage(percent: number, locale: string, decimals = 0, signed = false): string {
  return new Intl.NumberFormat(locale, {
    style: 'percent',
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
    signDisplay: signed ? 'exceptZero' : 'auto',
  }).format(percent / 100);
}

/** One piece of a percentage: the number, the sign, or the space a country puts between them. */
export interface PercentagePart {
  text: string;
  isSign: boolean;
}

/**
 * `formatPercentage` in pieces, for a design that draws the sign smaller than the number: "72" and
 * "%" in the States, "72", a no-break space and "%" in Germany. The pieces keep the locale's order.
 */
export function percentageParts(percent: number, locale: string): PercentagePart[] {
  return new Intl.NumberFormat(locale, {style: 'percent', maximumFractionDigits: 0})
    .formatToParts(percent / 100)
    .map(part => ({text: part.value, isSign: part.type === 'percentSign'}));
}

/** The decimal and thousands separators of a locale, for a library that takes them apart (Highcharts). */
export function numberSeparators(locale: string): {decimal: string; group: string} {
  const parts = new Intl.NumberFormat(locale).formatToParts(1234567.5);
  return {
    decimal: parts.find((part) => part.type === 'decimal')?.value ?? '.',
    group: parts.find((part) => part.type === 'group')?.value ?? ',',
  };
}
